import { getPagination, getTableFilter, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import {
  Button,
  Col,
  notification,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd'
import dayjs from 'dayjs'
import { capitalize, isEmpty, orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Can,
  Copyable,
  Icon,
  IconContainer,
  PageContainer,
  PageTitleRow,
} from '../../components'
import { withConfig } from '../../modules/'
import { configPropTypes } from '../../types'
import {
  createDownloadLink,
  handleBackendError,
  transformSearchParams,
} from '../../utils'
import ScanCancellationModal from './ScanCancellationModal'
import ScanDetails from './ScanDetails'
import ScanModal from './ScanModal'

import { getCountryList } from '../country-management/api'
import { exportScans, getScanList } from './api'

export const Scan = ({ config }) => {
  const statusOptions = orderBy(
    config.config?.scan_statuses.map((status) => ({
      text: status,
      value: status,
    })),
    'text',
    'DESC',
  )
  const types = orderBy(
    config.config?.user_types.map((type) => ({ text: type, value: type })),
    'text',
    'DESC',
  )
  const scanTypes = [
    { text: 'Admin', value: 'admin' },
    { text: 'User', value: 'user' },
  ]

  const [searchParams, setSearchParams] = useSearchParams()
  const [scans, setScans] = useState([])
  const [errorMessage, setErrorMessage] = useState()
  const [countries, setCountries] = useState([])
  const [selectedScan, setSelectedScan] = useState([])
  const [selectedBatchScans, setSelectedBatchScans] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isModalLoading, setModalLoading] = useState(false)
  const [isModalVisible, setModalVisibility] = useState(false)
  const [isDetailsVisible, setDetailsVisibility] = useState(false)
  const [isCancellationModalVisible, setCancellationModalVisibility] =
    useState(false)
  const [metaData, setMetaData] = useState()

  useEffect(() => {
    if (isEmpty(searchParams.toString()))
      setSearchParams({ items_per_page: 25, page: 1 })
  }, [])

  const statusColor = {
    cancelled: 'red',
    completed: 'green',
    failed: 'red',
    pending: 'blue',
    rejected: 'red',
  }

  useEffect(() => {
    getCountryList()
      .then(({ data }) => {
        const filters = orderBy(
          data.data.map(({ id, name }) => ({ id, text: name, value: name })),
          'text',
          'asc',
        )

        setCountries(filters)
      })
      .catch((error) => {
        handleFailure(error, 'Fetching countries failed!')
      })
  }, [])

  useEffect(() => {
    if (!isEmpty(searchParams.toString()))
      fetchScanList(searchParams.toString())
  }, [searchParams])

  const fetchScanList = (params) => {
    setIsLoading(true)

    getScanList(params)
      .then(({ data }) => {
        setScans(data.data)
        setMetaData(data.meta)
        setIsLoading(false)
      })
      .catch((error) => {
        setIsLoading(false)
        handleFailure(error, 'Fetching scan list failed!')
      })
  }

  const tableColumns = [
    {
      className: 'userIdSelenium',
      render: (_, { user_id }) => (
        <div style={{ maxWidth: 175 }}>
          <Copyable text={user_id} />
        </div>
      ),
      title: 'User Id',
      ...getTableFilter({ name: 'user_id_eq' }),
    },
    {
      className: 'scanTypeSelenium',
      dataIndex: 'scan_type',
      key: 'scan_type',
      render: (_, { scan_type }) => (
        <IconContainer>
          <Tooltip title={capitalize(scan_type)}>
            <Icon cursor="default" type={scan_type} />
          </Tooltip>
        </IconContainer>
      ),
      sorter: true,
      title: 'Scan type',
      ...getTableFilter({ filterValues: scanTypes, isMultiple: false, name: 'scan_type_eq', type: 'checkbox' }),
    },
    {
      className: 'statusSelenium',
      columnKey: 'status',
      dataIndex: 'status',
      key: 'status',
      render: (_, { status }) => (
        <Row gutter={8} justify="space-between" wrap={false}>
          <Col span={16}>
            <div
              style={{
                color: `${statusColor[status]}`,
                whiteSpace: 'nowrap',
              }}
            >
              {status}
            </div>
          </Col>
        </Row>
      ),
      sorter: true,
      title: 'Status',
      ...getTableFilter({ filterValues: statusOptions, name: 'status_in[]', type: 'checkbox' }),
    },
    {
      className: 'userTypeSelenium',
      render: (_, { user }) => (
        <IconContainer>
          <Tooltip title={capitalize(user.user_type)}>
            <Icon cursor="default" type={user.user_type} />
          </Tooltip>
        </IconContainer>
      ),
      title: 'User Type',
      ...getTableFilter({ filterValues: types, isMultiple: false, name: 'user_type_eq', type: 'checkbox' }),

    },
    {
      className: 'userNameSelenium',
      dataIndex: 'user_name',
      render: (_, { user }) => <span>{user.name}</span>,
      sorter: true,
      title: 'User name',
      ...getTableFilter({ name: 'user_name_cont' }),
    },
    {
      className: 'storeNameSelenium',
      dataIndex: 'store_name_cont',
      render: (_, record) => <span>{record.store_name}</span>,
      title: 'Store name',
      ...getTableFilter({ name: 'store_name_cont' }),
    },
    {
      className: 'countrySelenium',
      dataIndex: 'user_country_name',
      render: (_, { user }) => <span>{user.country_name}</span>,
      sorter: true,
      title: 'Country',
      ...getTableFilter({ filterValues: countries, isMultiple: false, name: 'user_country_name_eq', type: 'checkbox' }),
    },
    {
      className: 'createdAtSelenium',
      dataIndex: 'created_at',
      defaultSortOrder: 'descend',
      render: (dateTime) => <span>{dayjs(dateTime).format('DD.MM.YYYY')}</span>,
      sorter: true,
      title: 'Created at',
      ...getTableFilter({ name: 'created_at', type: 'date' }),
    },
    {
      className: 'finalizedAtSelenium',
      dataIndex: 'finalized_at',
      render: (dateTime) => (
        <span>{dateTime ? dayjs(dateTime).format('DD.MM.YYYY') : '-'}</span>
      ),
      sorter: true,
      title: 'Finalized at',
      ...getTableFilter({ name: 'finalized_at', type: 'date' }),
    },
    {
      className: 'amountSelenium',
      dataIndex: 'amount',
      key: 'amount',
      render: (_, { user, amount }) => (
        <div>
          {new Intl.NumberFormat(user.country_locales[0], {
            currency: user.currency_symbol,
            style: 'currency',
          }).format(amount)}
        </div>
      ),
      sorter: true,
      title: 'Amount',
    },
    {
      className: 'qrCodeSelenium',
      render: (_, record) => (
        <Tooltip title="Open scan details">
          <Can
            name="exportScansSelenium"
            no={() => <p>{record.qr_code}</p>}
            requiredPermission="scan.get.scan_id"
            yes={() => (
              <Button
                data-testid="scanDetailsBtn"
                name="qrCodeBtnSelenium"
                onClick={() => handleOpenDetailsPage(record)}
                type="link"
              >
                {record.qr_code}
              </Button>
            )}
          />
        </Tooltip>
      ),
      title: 'QR code',
      ...getTableFilter({ name: 'qr_code_cont' }),
    },
    {
      className: 'actionsSelenium',
      key: 'actions',
      render: (_, record) => (
        <Col>
          <IconContainer>
            {record.status === 'pending' && (
              <Can
                requiredPermission="scan.put.scan_id_cancel"
                yes={() => (
                  <Tooltip title="Cancel scan">
                    <Popconfirm
                      cancelText="No"
                      okText="Yes"
                      onConfirm={() => handleOpenCancellationPage([record.id])}
                      title="Are you sure you want to set scan status to 'cancelled'?"
                    >
                      <Icon
                        cursor="pointer"
                        data-testid="cancelIconSelenium"
                        name="cancelIconSelenium"
                        style={{ color: 'red' }}
                        type="closeCircle"
                      />
                    </Popconfirm>
                  </Tooltip>
                )}
              />
            )}
          </IconContainer>
        </Col>
      ),
      title: 'Actions',
    },
  ]

  const handleExportScansCSV = (type = 'export') => {
    exportScans(type, transformSearchParams(searchParams.toString(), ['created_at', 'finalized_at']))
      .then(({ blob, fileName }) => {
        createDownloadLink(blob, fileName)

        notification.success({
          description: 'Scans exported!',
          duration: 3,
          title: 'Success',
        })
      })
      .catch((error) => {
        handleFailure(error, 'Scans export failed!')
      })
  }

  const handleOpenModal = () => {
    setErrorMessage(null)
    setModalVisibility(true)
    setModalLoading(false)
  }

  const handleSuccess = (messageOptions, stayVisible = false) => {
    fetchScanList(searchParams.toString())

    if (!stayVisible) {
      setErrorMessage(null)
      setModalVisibility(false)
      setCancellationModalVisibility(false)
    }

    notification.success({
      title: 'Success',
      ...messageOptions,
    })
  }

  const handleFailure = (error, message) => {
    if (error) {
      console.log(error)
    }

    const errorMessage = handleBackendError(error, message)

    setErrorMessage(errorMessage)
    setModalLoading(false)

    notification.error({
      description: errorMessage,
      title: 'Error',
    })
  }

  const handleOpenDetailsPage = (selectedScan) => {
    setSelectedScan(selectedScan)
    setDetailsVisibility(true)
  }

  const handleOpenCancellationPage = (selectedScan) => {
    setSelectedScan(selectedScan)
    setCancellationModalVisibility(true)
  }

  const rowSelection = {
    getCheckboxProps: (record) => ({
      disabled: record.status !== 'pending',
    }),
    onChange: (selectedRowKeys) => {
      setSelectedBatchScans(selectedRowKeys)
    },
    selectedRowKeys: selectedBatchScans,
    type: 'checkbox',
  }

  const closeModalAndFetchList = (isSuccess = false) => {
    setCancellationModalVisibility(false)

    if (isSuccess) {
      fetchScanList(searchParams.toString())
      setSelectedBatchScans([])
    }
  }

  return (
    <PageContainer>
      <PageTitleRow title="Scans">
        <Space size="middle">
          <IconContainer>
            <Can
              requiredPermission="scan.put.scan_id_cancel"
              yes={() => (
                <Tooltip title="Cancel scan(s)">
                  <Button
                    danger
                    data-testid="batchCancelSelenium"
                    disabled={selectedBatchScans.length === 0}
                    name="batchCancelSelenium"
                    onClick={() =>
                      handleOpenCancellationPage(selectedBatchScans)
                    }
                  >
                    {`Cancel selected scans ${selectedBatchScans.length > 0 ? ` (${selectedBatchScans.length})` : ''}`}
                  </Button>
                </Tooltip>
              )}
            />
          </IconContainer>
          <Can
            name="exportScansSelenium"
            requiredPermission="scan.get.export"
            yes={() => (
              <Button
                data-testid="exportScansWithoutItemsSelenium"
                name="exportScansWithoutItemsSelenium"
                onClick={() => handleExportScansCSV()}
              >
                Export CSV without items
              </Button>
            )}
          />
          <Can
            name="exportScansWithItemsSelenium"
            requiredPermission="scan.get.export_with_items"
            yes={() => (
              <Button
                data-testid="exportScansWithItemsSelenium"
                name="exportScansWithItemsSelenium"
                onClick={() => handleExportScansCSV('export_with_items')}
              >
                Export CSV with items
              </Button>
            )}
          />
          <Can
            requiredPermission="scan.post.create"
            yes={() => (
              <Button
                data-testid="createScanSelenium"
                name="createScanSelenium"
                onClick={() => handleOpenModal()}
                type="primary"
              >
                NEW ADMIN SCAN
              </Button>
            )}
          />
        </Space>
      </PageTitleRow>
      <Row>
        <Col span={24} style={{ overflow: 'scroll' }}>
          <Table
            className="scanPageTableSelenium"
            columns={tableColumns}
            data-testid="scanPageTableSelenium"
            dataSource={scans}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParams)}
            pagination={getPagination(metaData?.page, metaData?.items_per_page, metaData?.total_items)}
            rowKey="id"
            rowSelection={rowSelection}
            scroll={{ x: true }}
          />

          {isModalVisible && (
            <ScanModal
              countries={countries}
              errorMessage={errorMessage}
              hideModal={() => setModalVisibility(false)}
              isModalLoading={isModalLoading}
              onSuccess={handleSuccess}
              setModalLoading={setModalLoading}
            />
          )}

          {isDetailsVisible && (
            <ScanDetails
              hideDetails={() => setDetailsVisibility(false)}
              selectedScan={selectedScan}
            />
          )}

          {isCancellationModalVisible && (
            <ScanCancellationModal
              hideModal={closeModalAndFetchList}
              selectedScanIds={selectedScan}
            />
          )}
        </Col>
      </Row>
    </PageContainer>
  )
}

Scan.propTypes = {
  config: configPropTypes,
}

export default withConfig(Scan)
